import { css, cx } from "@emotion/css";
import { TFunction } from "i18next";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { FeatureFlags } from "@/models/FeatureFlagModel.ts";
import { PatientEvent } from "@/models/PatientEventModel.ts";
import { doctorFormattedName } from "@/models/UserModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { toDateTime } from "@/utils/dateUtils.ts";

type PatientEventProps = {
  event: PatientEvent;
};

const getEventWording = (
  event: PatientEvent,
  isAuthorSelf: boolean,
  t: TFunction<"tl", undefined>,
  featureFlags?: FeatureFlags,
) => {
  const doctorName = doctorFormattedName(event.authorLastName, event.authorFirstName);

  if (event.eventType == "doctor_decided_not_to_renew") {
    const decidedNotToRenewKey = event.payload?.stoppedBeforeCpEnd
      ? isAuthorSelf
        ? "patientEvents.decidedNotToRenew.beforeEndCP.self"
        : "patientEvents.decidedNotToRenew.beforeEndCP.other"
      : isAuthorSelf
        ? "patientEvents.decidedNotToRenew.afterEndCP.self"
        : "patientEvents.decidedNotToRenew.afterEndCP.other";
    return t(decidedNotToRenewKey, { doctorName });
  }

  if (event.eventType == "careplan_renewed") {
    const carePlanRenewedKey = isAuthorSelf
      ? "patientEvents.carePlanRenewed.self"
      : "patientEvents.carePlanRenewed.other";
    return t(carePlanRenewedKey, { doctorName });
  }

  if (event.eventType == "care_plan_interrupted") {
    if (isAuthorSelf) {
      return t("patientEvents.careplanInterrupted.self");
    }
    // No practitioner name means the action was taken by Stēto CS (admin), not a doctor.
    if (!doctorName) {
      return t("patientEvents.careplanInterrupted.steto");
    }
    return t("patientEvents.careplanInterrupted.other", { doctorName });
  }

  if (event.eventType == "did_not_start" || event.eventType == "careplan_deleted") {
    return t("patientEvents.carePlanDeleted");
  }

  if (event.eventType == "rpm_started") {
    return t("patientEvents.rpmStarted");
  }

  if (event.eventType == "deceased") {
    return t("patientEvents.deceased");
  }

  if (featureFlags?.["show-aircall-call"]) {
    if (event.eventType == "aircall_call_answered") {
      return t("patientEvents.aircall.callAnswered", {
        duration: event.payload?.duration,
      });
    }
    if (event.eventType == "aircall_call_not_answered") {
      return t("patientEvents.aircall.callNotAnswered");
    }
  }

  if (featureFlags?.["show-aircall-sms"]) {
    if (event.eventType == "aircall_sms") {
      return t("patientEvents.aircall.smsSentBySteto");
    }
    if (event.eventType == "aircall_sms_from_patient") {
      return t("patientEvents.aircall.smsSentByPatient");
    }
  }

  if (event.eventType === "nurse_reinclusion_requested") {
    const key = isAuthorSelf
      ? "patientEvents.nurseReinclusion.selfRequested"
      : "patientEvents.nurseReinclusion.otherRequested";
    return t(key, { doctorName });
  } else if (event.eventType === "nurse_reinclusion_cancelled") {
    const key = isAuthorSelf
      ? "patientEvents.nurseReinclusion.selfCancelled"
      : "patientEvents.nurseReinclusion.otherCancelled";
    return t(key, { doctorName });
  } else if (event.eventType === "nurse_reinclusion_refused") {
    const key = isAuthorSelf
      ? "patientEvents.nurseReinclusion.selfRefused"
      : "patientEvents.nurseReinclusion.otherRefused";
    return t(key, { doctorName });
  }

  return "";
};

export const PatientEventCard = ({ event }: PatientEventProps) => {
  const me = Queries.user.useMe({});
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const { data: featureFlags } = Queries.user.useFeatureFlags({});

  const isAuthorSelf = me?.id === event.authorId;
  const eventText = getEventWording(event, isAuthorSelf, t, featureFlags);

  const timeSent = toDateTime(event.createdAt).toLocaleString(DateTime.TIME_SIMPLE);

  return (
    <Card className={cx(styles.card)}>
      <CardHeader
        className={cx(styles.headerContainer)}
        title={
          <>
            <Typography className={styles.title}>{t("patientEvents.header")}</Typography>
            <Typography variant="caption">{timeSent}</Typography>
          </>
        }
      />
      <Typography variant="body" className={styles.bodyContainer}>
        {eventText}
      </Typography>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
  }),
  senderContainer: css({
    justifyContent: "flex-end",
  }),
  card: css({
    display: "flex",
    flexDirection: "column",
    width: "85%",
    margin: theme.spacing(4),
    padding: theme.spacing(4),
    gap: theme.spacing(4),
  }),
  practitionerOnlyCard: css({
    backgroundColor: theme.palette.green.contrastText,
  }),
  senderCard: css({
    borderRadius: theme.spacing(4, 4, 0, 4),
  }),
  unreadCard: css({
    border: "1px solid",
    borderColor: `rgb(from ${theme.palette.primary.main} r g b / 0.2)`,
  }),
  headerContainer: css({
    padding: 0,
  }),
  title: css({
    fontWeight: "bold",
    marginRight: theme.spacing(4),
    fontStyle: "italic",
  }),
  bodyContainer: css({
    whiteSpace: "pre-wrap",
    maxWidth: "100%",
    fontStyle: "italic",
  }),
});
